'use client';

import { Box, Paper, Stack, Typography } from "@mui/material";
import Image from "next/image";
import { MouseEvent, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import LoadingSpinner from "@/app/__to_be_integrated/_____components/_NEW/LoadingSpinner";
import { MediaRef } from "@/app/features/shared/__to_be_integrated/types/general/media/media";
import {
    mediaRefCaption,
    mediaRefName
} from "@/app/features/shared/__to_be_integrated/types/general/media/media-helper";
import { MediaViewErrorFrame, MediaViewLoadingFrame, MediaViewMediaFrame } from "@/app/features/shared/media-view/ui/shared/MediaViewFrames";
import DetailsMenu from "@/app/features/shared/media-view/ui/shared/DetailsMenu";
import ImageView from "@/app/features/shared/media-view/ui/shared/media-views/ImageView";
import MagnifierGlass from "@/app/features/shared/media-view/ui/shared/MagnifierGlass";
import {
    parseObjectPositionToRatios,
    resolveObjectPositionOffsets
} from "@/app/features/shared/media-view/helper/magnifier-helper";
import { ImageMeta, MagnifierLayout, PendingMagnifierPointer } from "@/app/features/shared/media-view/types/types";

export interface SingleImageViewProps {
    image: MediaRef;
    magnifierEnabled?: boolean;
    imageInfoEnabled?: boolean;
}

const MODAL_IMAGE_WIDTH = 1080;
const MODAL_IMAGE_HEIGHT = 900;
const MODAL_CONTAINER_PADDING = '64px';
const MAGNIFIER_SIZE = 250;
const MAGNIFIER_ZOOM_LEVEL = 2.5;

enum ImageLoadState {
    LOADING = 'loading',
    LOADED = 'loaded',
    ERROR = 'error',
}

type SingleImageMagnifierLayout = MagnifierLayout & {
    imageOffsetX: number;
    imageOffsetY: number;
};

type VisibleImageBounds = {
    left: number;
    top: number;
    width: number;
    height: number;
};

export default function SingleImageView({
    image,
    magnifierEnabled,
    imageInfoEnabled,
}: SingleImageViewProps) {
    const [loadingState, setLoadingState] = useState<ImageLoadState>(ImageLoadState.LOADING);
    const [isMagnifying, setIsMagnifying] = useState(false);
    const [showMagnifierGlass, setShowMagnifierGlass] = useState(false);
    const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
    const [visibleImageBounds, setVisibleImageBounds] = useState<VisibleImageBounds | null>(null);

    const imageContainerRef = useRef<HTMLSpanElement | null>(null);
    const frameRef = useRef<number | null>(null);
    const pendingPointerRef = useRef<PendingMagnifierPointer | null>(null);
    const magnifierLayoutRef = useRef<SingleImageMagnifierLayout | null>(null);
    const magnifierGlassRef = useRef<HTMLDivElement | null>(null);

    const isMagnifierActive = Boolean(magnifierEnabled && isMagnifying && loadingState === ImageLoadState.LOADED);
    const showDetailsMenu = Boolean(magnifierEnabled || imageInfoEnabled);
    const imageMeta: ImageMeta | undefined = useMemo(() => {
        return {
            name: mediaRefName(image),
            caption: mediaRefCaption(image),
        };
    }, [image]);

    const cancelQueuedFrame = useCallback(() => {
        if (frameRef.current === null || typeof window === 'undefined') return;
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
    }, []);

    const applyMagnifierStyles = useCallback((mouseX: number, mouseY: number, bgX: number, bgY: number) => {
        const magnifierGlassEl = magnifierGlassRef.current;
        if (!magnifierGlassEl) return;
        magnifierGlassEl.style.setProperty('--media-view-magnifier-glass-left', `${mouseX - (MAGNIFIER_SIZE / 2)}px`);
        magnifierGlassEl.style.setProperty('--media-view-magnifier-glass-top', `${mouseY - (MAGNIFIER_SIZE / 2)}px`);
        magnifierGlassEl.style.setProperty('--media-view-magnifier-bg-x', `${bgX}px`);
        magnifierGlassEl.style.setProperty('--media-view-magnifier-bg-y', `${bgY}px`);
    }, []);

    const measureMagnifierLayout = useCallback((containerEl: HTMLElement, containerRect?: DOMRect): SingleImageMagnifierLayout | null => {
        const imgEl = containerEl.querySelector('img') as HTMLImageElement | null;
        if (!imgEl) return null;

        const nextContainerRect = containerRect ?? containerEl.getBoundingClientRect();
        const imgRect = imgEl.getBoundingClientRect();
        const computed = getComputedStyle(imgEl);

        let scaledW = imgRect.width || nextContainerRect.width;
        let scaledH = imgRect.height || nextContainerRect.height;
        let cropOffsetX = 0;
        let cropOffsetY = 0;
        let imageOffsetX = imgRect.left - nextContainerRect.left;
        let imageOffsetY = imgRect.top - nextContainerRect.top;

        if (imgEl.naturalWidth > 0 && imgEl.naturalHeight > 0) {
            const resolved = resolveObjectPositionOffsets(
                computed,
                imgRect.width || nextContainerRect.width,
                imgRect.height || nextContainerRect.height,
                imgEl.naturalWidth,
                imgEl.naturalHeight,
            );
            scaledW = resolved.scaledW;
            scaledH = resolved.scaledH;
            cropOffsetX = resolved.offsetX;
            cropOffsetY = resolved.offsetY;

            const objectPosition = parseObjectPositionToRatios(computed.objectPosition);
            const freeSpaceX = Math.max(0, (imgRect.width || nextContainerRect.width) - scaledW);
            const freeSpaceY = Math.max(0, (imgRect.height || nextContainerRect.height) - scaledH);
            imageOffsetX += freeSpaceX * objectPositionRatio(objectPosition.x, freeSpaceX);
            imageOffsetY += freeSpaceY * objectPositionRatio(objectPosition.y, freeSpaceY);
        }

        const nextLayout = {
            containerWidth: nextContainerRect.width,
            containerHeight: nextContainerRect.height,
            scaledW,
            scaledH,
            offsetX: cropOffsetX,
            offsetY: cropOffsetY,
            imageOffsetX,
            imageOffsetY,
        };

        magnifierLayoutRef.current = nextLayout;
        setVisibleImageBounds((previous) => {
            const nextBounds = {
                left: imageOffsetX,
                top: imageOffsetY,
                width: scaledW,
                height: scaledH,
            };

            if (
                previous
                && Math.abs(previous.left - nextBounds.left) < 0.5
                && Math.abs(previous.top - nextBounds.top) < 0.5
                && Math.abs(previous.width - nextBounds.width) < 0.5
                && Math.abs(previous.height - nextBounds.height) < 0.5
            ) {
                return previous;
            }

            return nextBounds;
        });
        setImageSize((previous) => {
            if (previous.width === scaledW && previous.height === scaledH) return previous;
            return { width: scaledW, height: scaledH };
        });

        return nextLayout;
    }, []);

    const updateMagnifierPosition = useCallback((containerEl: HTMLElement, clientX: number, clientY: number) => {
        const containerRect = containerEl.getBoundingClientRect();
        const hasSizeChanged = !magnifierLayoutRef.current
            || Math.abs(magnifierLayoutRef.current.containerWidth - containerRect.width) > 0.5
            || Math.abs(magnifierLayoutRef.current.containerHeight - containerRect.height) > 0.5;

        const layout = hasSizeChanged
            ? measureMagnifierLayout(containerEl, containerRect)
            : magnifierLayoutRef.current;

        if (!layout) return;

        const mouseXInContainer = clientX - containerRect.left;
        const mouseYInContainer = clientY - containerRect.top;
        const mouseXInVisibleImage = mouseXInContainer - layout.imageOffsetX;
        const mouseYInVisibleImage = mouseYInContainer - layout.imageOffsetY;

        if (
            mouseXInVisibleImage < 0
            || mouseYInVisibleImage < 0
            || mouseXInVisibleImage > layout.scaledW
            || mouseYInVisibleImage > layout.scaledH
        ) {
            setShowMagnifierGlass(false);
            return;
        }

        const relXInScaledImage = mouseXInVisibleImage + layout.offsetX;
        const relYInScaledImage = mouseYInVisibleImage + layout.offsetY;

        applyMagnifierStyles(
            mouseXInContainer,
            mouseYInContainer,
            -relXInScaledImage * MAGNIFIER_ZOOM_LEVEL + (MAGNIFIER_SIZE / 2),
            -relYInScaledImage * MAGNIFIER_ZOOM_LEVEL + (MAGNIFIER_SIZE / 2),
        );
    }, [applyMagnifierStyles, measureMagnifierLayout]);

    const flushQueuedMagnifierUpdate = useCallback(() => {
        frameRef.current = null;
        const pendingPointer = pendingPointerRef.current;
        if (!pendingPointer) return;
        updateMagnifierPosition(pendingPointer.containerEl, pendingPointer.clientX, pendingPointer.clientY);
    }, [updateMagnifierPosition]);

    const queueMagnifierUpdate = useCallback((containerEl: HTMLElement, clientX: number, clientY: number) => {
        pendingPointerRef.current = { containerEl, clientX, clientY };
        if (typeof window === 'undefined') return;
        if (frameRef.current !== null) return;
        frameRef.current = window.requestAnimationFrame(flushQueuedMagnifierUpdate);
    }, [flushQueuedMagnifierUpdate]);

    useEffect(() => {
        return () => {
            cancelQueuedFrame();
            pendingPointerRef.current = null;
        };
    }, [cancelQueuedFrame]);

    const handleMouseEnter = useCallback((event: MouseEvent<HTMLSpanElement>) => {
        if (!isMagnifierActive) return;
        measureMagnifierLayout(event.currentTarget);
        setShowMagnifierGlass(true);
        queueMagnifierUpdate(event.currentTarget, event.clientX, event.clientY);
    }, [isMagnifierActive, measureMagnifierLayout, queueMagnifierUpdate]);

    const handleMouseLeave = useCallback(() => {
        setShowMagnifierGlass(false);
        pendingPointerRef.current = null;
        cancelQueuedFrame();
    }, [cancelQueuedFrame]);

    const handleMouseMove = useCallback((event: MouseEvent<HTMLSpanElement>) => {
        if (!isMagnifierActive) return;
        if (!showMagnifierGlass) {
            setShowMagnifierGlass(true);
        }
        queueMagnifierUpdate(event.currentTarget, event.clientX, event.clientY);
    }, [isMagnifierActive, queueMagnifierUpdate, showMagnifierGlass]);

    const onLoadImage = useCallback((event: SyntheticEvent<HTMLImageElement, Event>) => {
        setLoadingState(ImageLoadState.LOADED);
        const nextImage = event.currentTarget;
        setImageSize((previous) => {
            if (previous.width === nextImage.naturalWidth && previous.height === nextImage.naturalHeight) {
                return previous;
            }

            return {
                width: nextImage.naturalWidth,
                height: nextImage.naturalHeight,
            };
        });
        const containerEl = imageContainerRef.current;
        if (containerEl) {
            measureMagnifierLayout(containerEl);
        }
    }, [measureMagnifierLayout]);

    const onLoadImageError = useCallback(() => {
        setLoadingState(ImageLoadState.ERROR);
        setIsMagnifying(false);
        setShowMagnifierGlass(false);
        cancelQueuedFrame();
        pendingPointerRef.current = null;
    }, [cancelQueuedFrame]);

    const onMagnifierError = useCallback(() => {
        setIsMagnifying(false);
        setShowMagnifierGlass(false);
    }, []);

    return (
        <Paper
            sx={{
                display: 'flex',
                flex: '1 1 0',
                p: 2,
                width: `min(${MODAL_IMAGE_WIDTH}px, calc(95vw - ${MODAL_CONTAINER_PADDING}))`,
                height: `min(${MODAL_IMAGE_HEIGHT}px, calc(95vh - ${MODAL_CONTAINER_PADDING}))`,
                maxWidth: `calc(95vw - ${MODAL_CONTAINER_PADDING})`,
                maxHeight: `calc(95vh - ${MODAL_CONTAINER_PADDING})`,
                minWidth: 0,
                minHeight: 0,
                boxShadow: 24,
                overflow: 'hidden',
            }}
        >
            <Box
                component="span"
                className="relative flex flex-auto size-full media-view-image"
                sx={{
                    position: 'relative',
                    justifyContent: 'center',
                    alignItems: 'center',
                    display: 'flex',
                    flex: '1 1 auto',
                    minWidth: 0,
                    minHeight: 0,
                    maxWidth: '100%',
                    maxHeight: '100%',
                }}
            >
                <MediaViewMediaFrame isVisible={loadingState === ImageLoadState.LOADED}>
                    <Box
                        ref={imageContainerRef}
                        component="span"
                        className="relative flex flex-auto size-full media-view-image-inner"
                        onMouseLeave={handleMouseLeave}
                        onMouseEnter={handleMouseEnter}
                        onMouseMove={handleMouseMove}
                        sx={{
                            position: 'relative',
                            justifyContent: 'center',
                            zIndex: 100,
                            maxWidth: '100%',
                            maxHeight: '100%',
                        }}
                    >
                        <ImageView
                            src={image}
                            width={MODAL_IMAGE_WIDTH}
                            height={MODAL_IMAGE_HEIGHT}
                            onLoad={onLoadImage}
                            onError={onLoadImageError}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                            }}
                        />
                        {isMagnifierActive && showMagnifierGlass && (
                            <MagnifierGlass
                                glassRef={magnifierGlassRef}
                                image={image}
                                imageSize={imageSize}
                                zoomLevel={MAGNIFIER_ZOOM_LEVEL}
                                size={MAGNIFIER_SIZE}
                                onError={onMagnifierError}
                            />
                        )}
                    </Box>
                </MediaViewMediaFrame>

                {loadingState === ImageLoadState.LOADING && (
                    <MediaViewLoadingFrame isVisible>
                        <LoadingSpinner />
                    </MediaViewLoadingFrame>
                )}

                {loadingState === ImageLoadState.ERROR && (
                    <MediaViewErrorFrame isVisible>
                        <ErrorView />
                    </MediaViewErrorFrame>
                )}

                {showDetailsMenu && loadingState !== ImageLoadState.ERROR && visibleImageBounds && (
                    <Box
                        component="span"
                        sx={{
                            position: 'absolute',
                            left: `${visibleImageBounds.left}px`,
                            top: `${visibleImageBounds.top}px`,
                            width: `${visibleImageBounds.width}px`,
                            height: `${visibleImageBounds.height}px`,
                            maxWidth: '100%',
                            maxHeight: '100%',
                            zIndex: 500,
                            pointerEvents: 'none',
                        }}
                    >
                        <Box component="span" sx={{ pointerEvents: 'auto' }}>
                            <DetailsMenu
                                imageMeta={imageInfoEnabled ? imageMeta : undefined}
                                position="top right"
                                magnifierEnabled={magnifierEnabled}
                                isMagnifying={isMagnifierActive}
                                setIsMagnifying={setIsMagnifying}
                            />
                        </Box>
                    </Box>
                )}
            </Box>
        </Paper>
    )
}

function ErrorView() {
    return (
        <Stack spacing={1} alignItems="center" justifyContent="center">
            <Image src="/assets/placeholder/media-error-error.svg" alt="Fehler beim Laden der Medienansicht" width={150} height={100} />
            <Typography color="error" variant="overline">Fehler beim Laden</Typography>
        </Stack>
    );
}

function objectPositionRatio(token: number | string, freeSpace: number): number {
    if (typeof token === 'number') return token;
    if (token.endsWith('px')) {
        const px = parseFloat(token);
        return freeSpace > 0 ? Math.max(0, Math.min(1, px / freeSpace)) : 0.5;
    }

    return 0.5;
}
